import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { CheckCircle, HourglassEmpty } from "@mui/icons-material";
import MathContent from "./MathContent";

/**
 * Renders a question card with math-formatted body, options, and explanation.
 * Used on all question list/review pages (teacher, reviewer, admin).
 *
 * Props:
 *   question    - question object { body, options, explanation, is_approved, updated_at, created_at }
 *   onClick     - click handler for the card
 *   topBadge    - optional JSX rendered above the card (e.g. review count badges)
 *   showStatus  - whether to show the approved/pending status chip (default true)
 *   formatDate  - function(dateString) => string
 */
const QuestionListCard = ({ question, onClick, topBadge, showStatus = true, formatDate }) => {
  const dateStr = formatDate ? formatDate(question.updated_at || question.created_at) : "";

  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
        border: `1px solid ${theme.palette.mode === "dark" ? "#3a3a3a" : "#DAD9D7"}`,
        borderRadius: "11px",
        mb: ".7em",
        p: { xs: "1em", md: "1.25em 2em" },
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { borderColor: theme.palette.mode === "dark" ? "#666" : "#aaa" } : {},
      })}
    >
      {topBadge}

      {/* Question body */}
      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", mb: 1, lineHeight: 1.6 }}>
        <MathContent html={question.body} />
      </Typography>

      {/* Options */}
      {question.options?.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1, pl: 1 }}>
          {question.options.map((opt, idx) => (
            <Box key={opt.id ?? idx} sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography component="span" sx={{ fontSize: "0.8rem", fontWeight: 600, color: opt.is_answer ? "#27AE60" : "text.disabled", minWidth: "1.4em" }}>
                {String.fromCharCode(65 + idx)}.
              </Typography>
              <Typography component="span" sx={{ fontSize: "0.85rem", color: opt.is_answer ? "#27AE60" : "text.secondary", fontWeight: opt.is_answer ? 600 : 400 }}>
                <MathContent html={opt.body} />
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Explanation */}
      {question.explanation && (
        <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", fontStyle: "italic", mb: 0.75 }}>
          <MathContent html={question.explanation} />
        </Typography>
      )}

      {/* Footer: date + status */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
          {dateStr}
        </Typography>
        {showStatus && (
          <Chip
            icon={question.is_approved ? <CheckCircle sx={{ fontSize: 14 }} /> : <HourglassEmpty sx={{ fontSize: 14 }} />}
            label={question.is_approved ? "Approved" : "Pending"}
            size="small"
            sx={(theme) => ({
              fontSize: "0.72rem",
              height: 22,
              backgroundColor: question.is_approved
                ? theme.palette.mode === "dark" ? "#1b3a27" : "#E8F5E9"
                : theme.palette.mode === "dark" ? "#3a2e10" : "#FFF8E1",
              color: question.is_approved ? "#4caf50" : "#FFA726",
              "& .MuiChip-icon": { color: question.is_approved ? "#4caf50" : "#FFA726" },
            })}
          />
        )}
      </Box>
    </Box>
  );
};

export default QuestionListCard;
